/* ═══════════════════════════════════════════════════════════
   FRAWGRAPHY — Fashion Photography Studio
   Tokens → Base → Header → Hero → Sections → Components → Motion
   ═══════════════════════════════════════════════════════════ */

/* ── Tokens: koyu tema (varsayılan) ───────────────────────── */
:root {
  --bg:          #060606;
  --bg-elev:     #0c0c0c;
  --bg-elev-2:   #121212;

  --fg:          #f4f2ef;
  --fg-muted:    #a5a29c;
  --fg-dim:      #85827c;

  --accent:      #c9b28a;
  --accent-soft: rgba(201, 178, 138, 0.16);

  --line:        rgba(244, 242, 239, 0.10);
  --line-strong: rgba(244, 242, 239, 0.26);

  --danger:      #ef6461;
  --success:     #8fbf87;

  --header-bg:   rgba(6, 6, 6, 0.82);
  --scrim:       rgba(6, 6, 6, 0.97);

  --img-grade:   grayscale(1) brightness(0.72) contrast(1.1);
  --img-grade-hover: grayscale(0) brightness(0.92) contrast(1.05);

  /* Tipografi — üç aile, üç net rol */
  --font-display: "Bodoni Moda", "Didot", "Times New Roman", serif;
  /* Inter en başta durmak zorunda. Helvetica daha önce geldiğinde, "Helvetica"
     kurulu olan her makinede (yazı tipi paketi kurmuş herkes) sıra Inter'a hiç
     gelmiyor: sayfa, indirdiği yazı tipiyle değil, elde kurulmuş bir Helvetica
     kopyasıyla çiziliyor. Ölçüldü 2026-09-19 — bu makinede Helvetica.ttf kurulu,
     yani gövde metni tasarlandığı yazı tipinde hiç görünmemiş. */
  --font-body:    "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif;
  --font-mono:    "JetBrains Mono", "SF Mono", ui-monospace, monospace;

  --step--1: clamp(0.75rem, 0.72rem + 0.15vw, 0.82rem);
  --step-0:  clamp(0.95rem, 0.91rem + 0.2vw,  1.05rem);
  /* Gövde ile lede arası: kart/hizmet/iletişim açıklamaları */
  --step-05: clamp(1rem,    0.95rem + 0.3vw,  1.16rem);
  --step-1:  clamp(1.1rem,  1.02rem + 0.4vw,  1.35rem);
  /* Lede ile başlık arası: uzun metinde okunan giriş ve alıntılar.
     --step-2 başlık ölçüsü olduğu için gövde metni oraya çıkamıyor.
     -15 hero lede'i, -175 yazı gövdesindeki giriş ve alıntıları taşır. */
  --step-15:  clamp(1.25rem, 1.14rem + 0.55vw, 1.6rem);
  --step-175: clamp(1.4rem,  1.28rem + 0.6vw,  1.8rem);
  --step-2:  clamp(1.5rem,  1.3rem  + 1vw,    2.25rem);
  --step-3:  clamp(2.2rem,  1.7rem  + 2.4vw,  4rem);
  --step-4:  clamp(3rem,    1.6rem  + 7vw,    9.5rem);

  --sp-1: 0.25rem; --sp-2: 0.5rem;  --sp-3: 0.75rem; --sp-4: 1rem;
  --sp-6: 1.5rem;  --sp-8: 2rem;    --sp-12: 3rem;   --sp-16: 4rem;
  --sp-24: 6rem;   --sp-32: 8rem;

  --shell: 1440px;
  --gutter: clamp(1.25rem, 4vw, 4.5rem);

  --ease: cubic-bezier(0.16, 1, 0.3, 1);
  --dur-fast: 180ms;
  --dur: 320ms;
  --dur-slow: 720ms;

  /* Header, mobil menünün üstünde kalmalı: kapatma düğmesi hep erişilebilir */
  --z-base: 0; --z-raised: 10; --z-menu: 60; --z-header: 80;
  --z-top: 100; --z-lightbox: 110; --z-loader: 120;

  color-scheme: dark;
}

/* ── Tokens: aydınlık tema ────────────────────────────────── */
:root[data-theme="light"] {
  --bg:          #f5f3f0;
  --bg-elev:     #ecebe6;
  --bg-elev-2:   #e3e1db;

  --fg:          #0b0b0a;
  --fg-muted:    #55524d;
  --fg-dim:      #6e6b65;

  /* Bronz aksan; en koyu yüzeyde bile 4.9:1 (WCAG AA) */
  --accent:      #775a24;
  --accent-soft: rgba(119, 90, 36, 0.13);

  --line:        rgba(11, 11, 10, 0.14);
  --line-strong: rgba(11, 11, 10, 0.32);

  --danger:      #a3231c;
  --success:     #37632f;

  --header-bg:   rgba(245, 243, 240, 0.86);
  --scrim:       rgba(245, 243, 240, 0.97);

  --img-grade:   grayscale(1) brightness(0.96) contrast(1.04);
  --img-grade-hover: grayscale(0) brightness(1) contrast(1);

  color-scheme: light;
}

/* ── Reset & base ─────────────────────────────────────────── */
*, *::before, *::after { box-sizing: border-box; }
* { margin: 0; }

html {
  -webkit-text-size-adjust: 100%;
  scroll-behavior: smooth;
  scroll-padding-top: 6rem;
}

body {
  background: var(--bg);
  color: var(--fg);
  font-family: var(--font-body);
  font-size: var(--step-0);
  line-height: 1.6;
  letter-spacing: 0.005em;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);
}

img { display: block; max-width: 100%; height: auto; }
ul, ol { list-style: none; padding: 0; }
a { color: inherit; text-decoration: none; }
button, input, select, textarea { font: inherit; color: inherit; }

:where(a, button, input, select, textarea, [tabindex]):focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 3px;
}

.visually-hidden {
  position: absolute; width: 1px; height: 1px;
  padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}

/* Bal küpü — .visually-hidden ile karıştırılmamalı: o, metni ekran
   okuyucuya BIRAKIR. Bu alan hiç kimseye görünmemeli, çünkü onu dolduran
   herkes bottur. display:none bazı botlarca atlandığı için ekran dışına
   taşınır; erişilebilirlik ağacından aria-hidden, sekme sırasından
   tabindex="-1" çıkarır (ikisi de HTML tarafında). */
.honeypot {
  position: absolute; left: -9999px; top: auto;
  width: 1px; height: 1px; overflow: hidden;
}

.skip-link {
  position: fixed; top: 0; left: 0; z-index: var(--z-top);
  padding: var(--sp-3) var(--sp-6);
  background: var(--fg); color: var(--bg);
  font-family: var(--font-mono); font-size: var(--step--1);
  transform: translateY(-120%);
  transition: transform var(--dur) var(--ease);
}
.skip-link:focus-visible { transform: translateY(0); }

.shell {
  width: 100%;
  max-width: var(--shell);
  margin-inline: auto;
  padding-inline: var(--gutter);
}

/* Ortak mikro-tipografi */
.eyebrow, .section-index, .card-cat, .card-year,
.chip span, .service-no, .service-meta, .footer-legal, .video-time {
  font-family: var(--font-mono);
  font-size: var(--step--1);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
}

/* ── Scroll progress ──────────────────────────────────────── */
.scroll-progress {
  position: fixed; inset: 0 0 auto 0; height: 2px;
  z-index: var(--z-top); pointer-events: none;
  background: var(--line);
}
.scroll-progress span {
  display: block; height: 100%; width: 0;
  background: var(--accent);
}

/* ── Sayfa yükleyici: spinner → analog objektif → flaş ────────
   Sıra: beyaz halka tek tur döner, rastgele slogan sözcük sözcük
   dönüşe yedirilir → halka kapanıp analog objektife dönüşür
   (tırtıllı odak halkası, gövde kademeleri, iris, aksan noktası) →
   slogan FRAWGRAPHY'ye dönüşür → flaş patlar ve sayfa açılır.
   Yükleyici temadan bağımsız koyudur (hero ile aynı zemin). */
html[data-loading] { overflow: hidden; }

.loader {
  position: fixed; inset: 0; z-index: var(--z-loader);
  display: grid; place-items: center;
  background: #060606;
  animation: ldr-clear 400ms var(--ease) 2300ms forwards;
}
.loader[data-done] { visibility: hidden; pointer-events: none; }
/* Aynı oturumda tekrar girişte hiç boyanmaz (yalnızca ilk giriş) */
.loader[data-skip] { display: none; }

/* Amblem + yazı birlikte söner; flaş kapatırken kaybolurlar */
.ldr-stack {
  display: grid; justify-items: center; gap: 18px;
  animation: ldr-mark-out 220ms linear 2320ms forwards;
}
.ldr { width: clamp(96px, 14vw, 132px); height: auto; }
.ldr-knurl, .ldr-ring, .ldr-step, .ldr-inner, .ldr-blades { fill: none; }

/* 1) Spinner: %76'lık yay, tepe noktasından tek tam tur */
.ldr-ring {
  stroke: #f4f2ef; stroke-width: 2.5; stroke-linecap: round;
  stroke-dasharray: 210 66.5;             /* r=44 → çevre 276.5 */
  transform-box: fill-box; transform-origin: center;
  transform: rotate(-90deg);
  animation:
    ldr-spin   850ms cubic-bezier(0.45, 0.05, 0.25, 1) forwards,
    ldr-close  320ms ease-out 800ms forwards,
    ldr-barrel 350ms var(--ease) 1080ms forwards;
}

/* 2) Analog objektif parçaları: halka kapanınca belirirler */
.ldr-knurl, .ldr-step, .ldr-inner, .ldr-glass,
.ldr-blades, .ldr-glint, .ldr-dot { opacity: 0; }

/* Tırtıllı odak halkası: kısa çizgili daire tik dokusu verir
   (çevre 307.9 / 36 tik → periyot 8.55) */
.ldr-knurl {
  stroke: rgba(244, 242, 239, 0.5); stroke-width: 4;
  stroke-dasharray: 1.3 7.25;
  transform-box: fill-box; transform-origin: center;
  transform: rotate(-18deg) scale(1.08);
  animation: ldr-knurl-in 380ms var(--ease) 1120ms forwards;
}
.ldr-step {
  stroke: rgba(244, 242, 239, 0.28); stroke-width: 1;
  transform-box: fill-box; transform-origin: center;
  transform: scale(1.1);
  animation: ldr-part-in 300ms var(--ease) 1180ms forwards;
}
.ldr-inner {
  stroke: rgba(244, 242, 239, 0.55); stroke-width: 1.4;
  transform-box: fill-box; transform-origin: center;
  transform: scale(1.14);
  animation: ldr-part-in 320ms var(--ease) 1140ms forwards;
}
.ldr-glass {
  fill: rgba(244, 242, 239, 0.07);
  stroke: rgba(244, 242, 239, 0.3); stroke-width: 1;
  transform-box: fill-box; transform-origin: center;
  transform: scale(1.2);
  animation: ldr-part-in 320ms var(--ease) 1200ms forwards;
}
.ldr-blades {
  stroke: rgba(244, 242, 239, 0.8); stroke-width: 1.3; stroke-linejoin: round;
  transform-box: fill-box; transform-origin: center;
  transform: rotate(-40deg) scale(1.25);
  animation: ldr-iris-in 380ms var(--ease) 1200ms forwards;
}
.ldr-glint {
  fill: rgba(255, 255, 255, 0.85);
  animation: ldr-part-in 240ms var(--ease) 1320ms forwards;
}
/* Analog gövdedeki kırmızı hiza noktasının marka rengiyle karşılığı */
.ldr-dot {
  fill: #c9b28a;
  animation: ldr-part-in 240ms var(--ease) 1300ms forwards;
}

/* 3) Yazı: slogan dönüşe yedirilir, sonra markaya dönüşür */
.ldr-text {
  position: relative; overflow: hidden;
  height: 1.5em; min-width: 280px; text-align: center;
  font-family: var(--font-mono); font-size: 0.78rem;
  letter-spacing: 0.22em;
  color: rgba(244, 242, 239, 0.78);
}
.ldr-phrase { display: block; white-space: nowrap; }
/* Sözcükler dönüşe yedirilir: kelime kelime aşağıdan yükselir */
.ldr-phrase .w {
  display: inline-block; opacity: 0; transform: translateY(0.7em);
  animation: ldr-word-in 380ms var(--ease) calc(300ms + var(--wi) * 170ms) forwards;
}
.ldr-brand {
  position: absolute; inset: 0; display: block; white-space: nowrap;
  font-family: var(--font-display); font-size: 1.05rem; font-weight: 700;
  letter-spacing: 0.34em; padding-left: 0.34em; /* izleme sağ boşluğunu dengeler */
  text-transform: uppercase; color: #f4f2ef;
}

/* Slogan → marka geçişi: karakter karakter, bulanıklıkla.
   .w opaklığı giriş için, .c opaklığı çıkış için — ikisi çarpılır, çakışmaz. */
.ldr-phrase .c, .ldr-brand .c { display: inline-block; white-space: pre; }

.ldr-phrase .c {
  animation: ldr-char-out 240ms var(--ease) calc(1450ms + var(--ci) * 12ms) forwards;
}
.ldr-brand .c {
  opacity: 0; transform: translateY(5px); filter: blur(5px);
  animation: ldr-char-in 300ms var(--ease) calc(1650ms + var(--ci) * 28ms) forwards;
}

/* 4) Flaş: tek patlama (WCAG 2.3.1 — saniyede 3 eşiğinin çok altında) */
.ldr-flash {
  position: absolute; inset: 0;
  background: #fff; opacity: 0;
  animation: ldr-flash 520ms cubic-bezier(0.16, 1, 0.3, 1) 2260ms forwards;
}

@keyframes ldr-spin       { to { transform: rotate(270deg); } }
@keyframes ldr-close      { to { stroke-dasharray: 276.5 0; } }
@keyframes ldr-barrel     { to { stroke-width: 3.4; } }
@keyframes ldr-knurl-in   { to { opacity: 1; transform: rotate(0deg) scale(1); } }
@keyframes ldr-part-in    { to { opacity: 1; transform: none; } }
@keyframes ldr-iris-in    { to { opacity: 1; transform: rotate(0deg) scale(1); } }
@keyframes ldr-word-in    { to { opacity: 1; transform: none; } }
@keyframes ldr-char-out   { to { opacity: 0; transform: translateY(-5px); filter: blur(5px); } }
@keyframes ldr-char-in    { to { opacity: 1; transform: translateY(0); filter: blur(0); } }
@keyframes ldr-flash      { 0% { opacity: 0; } 12% { opacity: 1; } 100% { opacity: 0; } }
@keyframes ldr-mark-out   { to { opacity: 0; } }
@keyframes ldr-clear      { to { background: transparent; } }

/* Hareket azaltma tercihinde yükleyici hiç gösterilmez */
@media (prefers-reduced-motion: reduce) {
  .loader { display: none; }
}

/* ── Header ───────────────────────────────────────────────── */
.site-header {
  position: fixed; inset: 0 0 auto 0; z-index: var(--z-header);
  padding-block: var(--sp-4);
  border-bottom: 1px solid transparent;
  transition: background var(--dur) var(--ease),
              border-color var(--dur) var(--ease),
              padding var(--dur) var(--ease);
}
.site-header[data-scrolled], .site-header--solid {
  background: var(--header-bg);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom-color: var(--line);
  padding-block: var(--sp-2);
}

/* Hero'nun üzerindeyken header koyu bir ada gibi davranır */
.site-header:not([data-scrolled]):not(.site-header--solid) {
  --fg: #f4f2ef;
  --fg-muted: #a5a29c;
  --fg-dim: #85827c;
  --accent: #c9b28a;
  --bg: #060606;
  --line-strong: rgba(244, 242, 239, 0.28);
  color: #f4f2ef;
}

.header-inner {
  display: flex; align-items: center; justify-content: space-between;
  gap: var(--sp-6);
}

.brand { display: grid; gap: 2px; min-height: 44px; align-content: center; }
.brand-mark { font-weight: 600; font-size: var(--step-0); letter-spacing: 0.24em; }
.brand-sub {
  font-family: var(--font-mono);
  font-size: 0.66rem; letter-spacing: 0.3em; text-transform: uppercase;
  color: var(--fg-dim);
}

.tabs-list { position: relative; display: flex; gap: clamp(0.1rem, 0.8vw, 0.9rem); }
.tab {
  position: relative; display: flex; align-items: baseline; gap: var(--sp-2);
  min-height: 44px; padding: var(--sp-3);
  color: var(--fg-muted); cursor: pointer;
  transition: color var(--dur) var(--ease);
}
.tab:hover, .tab:focus-visible, .tab[aria-current="true"] { color: var(--fg); }
.tab-label {
  font-size: var(--step--1); letter-spacing: 0.18em; text-transform: uppercase;
  font-weight: 500; white-space: nowrap;
}
.tab-indicator {
  position: absolute; bottom: 4px; left: 0;
  height: 1px; width: 0; opacity: 0;
  background: var(--accent);
  transition: transform var(--dur-slow) var(--ease), width var(--dur-slow) var(--ease), opacity var(--dur) var(--ease);
}
.tab-indicator[data-visible] { opacity: 1; }

.header-tools { display: flex; align-items: center; gap: var(--sp-3); }

/* Dil anahtarı */
.lang-switch { display: flex; border: 1px solid var(--line-strong); }
.lang-switch button {
  min-width: 40px; min-height: 44px; padding: 0 var(--sp-2);
  background: none; border: 0; cursor: pointer;
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.14em; color: var(--fg-dim);
  transition: color var(--dur) var(--ease), background var(--dur) var(--ease);
}
.lang-switch button + button { border-left: 1px solid var(--line-strong); }
.lang-switch button:hover { color: var(--fg); }
.lang-switch button[aria-pressed="true"] { background: var(--fg); color: var(--bg); }

/* Tema düğmesi ilk açılışta gizli; hero geçildiğinde belirir.
   visibility:hidden aynı zamanda sekme sırasından da çıkarır. */
.theme-toggle {
  display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px;
  background: none; border: 1px solid var(--line-strong);
  cursor: pointer;
  visibility: hidden; opacity: 0; transform: scale(0.86);
  transition: border-color var(--dur) var(--ease), background var(--dur) var(--ease),
              opacity var(--dur) var(--ease), transform var(--dur) var(--ease),
              visibility 0s linear var(--dur);
}
.site-header[data-past-hero] .theme-toggle,
.site-header--solid .theme-toggle {
  visibility: visible; opacity: 1; transform: none;
  transition-delay: 0s;
}
.theme-toggle:hover { border-color: var(--accent); background: var(--accent-soft); }
.theme-toggle svg {
  width: 18px; height: 18px;
  fill: none; stroke: currentColor; stroke-width: 1.4;
  stroke-linecap: round; stroke-linejoin: round;
}
.theme-toggle .icon-sun { display: none; }
:root[data-theme="dark"] .theme-toggle .icon-sun { display: block; }
:root[data-theme="dark"] .theme-toggle .icon-moon { display: none; }

.menu-toggle {
  display: none; align-items: center; gap: var(--sp-3);
  min-height: 44px; padding: var(--sp-2) var(--sp-3);
  background: none; border: 1px solid var(--line-strong); cursor: pointer;
}
.menu-toggle-label {
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.18em; text-transform: uppercase;
}
.menu-toggle-bars { display: grid; gap: 5px; width: 20px; }
.menu-toggle-bars i {
  display: block; height: 1px; background: currentColor;
  transition: transform var(--dur) var(--ease);
}
.menu-toggle[aria-expanded="true"] .menu-toggle-bars i:first-child { transform: translateY(3px) rotate(45deg); }
.menu-toggle[aria-expanded="true"] .menu-toggle-bars i:last-child  { transform: translateY(-3px) rotate(-45deg); }

/* ── Mobil menü ───────────────────────────────────────────── */
.mobile-menu {
  position: fixed; inset: 0; z-index: var(--z-menu);
  display: flex; flex-direction: column; justify-content: space-between;
  padding: 7rem var(--gutter) calc(var(--sp-8) + env(safe-area-inset-bottom));
  background: var(--scrim);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  overflow-y: auto;
}
.mobile-menu[hidden] { display: none; }
.mobile-menu-list a {
  display: flex; align-items: baseline;
  padding: var(--sp-3) 0;
  border-bottom: 1px solid var(--line);
}
.mobile-menu-list em {
  font-family: var(--font-display); font-style: normal;
  font-size: clamp(1.6rem, 7vw, 2.4rem); line-height: 1.15;
}
.mobile-menu-foot {
  margin-top: var(--sp-8);
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.14em; color: var(--fg-dim);
}

/* ── Hero: her iki temada da koyu bir ada ─────────────────── */
.hero {
  --fg: #f4f2ef;
  --fg-muted: #a5a29c;
  /* Lede'nin kendi tonu. --fg-muted, %100 ölçeklenen ekranlarda —
     yani masaüstü monitörlerin çoğunda — fotoğrafın üstünde dağılıyordu.
     Ölçüldü 2026-09-20: aynı paragraf 125% ölçeklenen dizüstünde 32,
     %100'deki 2560x1440 monitörde 25.6 aygıt pikseliyle çiziliyor; aynı
     fiziksel boy, dörtte bir daha az piksel. Çare ağırlık değil kontrast:
     ~4.7:1 iken ~8:1. Yalnızca bu paragraf — .btn--ghost de --fg-muted
     kullanıyor ve o dokunulmadan kalsın diye ayrı belirteç. */
  --fg-lede: #d8d4cd;
  --fg-dim: #85827c;
  --accent: #c9b28a;
  --bg: #060606;
  --line-strong: rgba(244, 242, 239, 0.26);

  position: relative;
  min-height: 100svh;
  display: grid; align-items: end;
  padding-block: 8rem var(--sp-16);
  overflow: hidden;
  isolation: isolate;
  background: #060606;
  color: #f4f2ef;
}

.hero-stage { position: absolute; inset: 0; z-index: -1; }

.hero-plate {
  position: absolute; inset: -6%; margin: 0;
  background: linear-gradient(140deg, #141414, #060606);
}
.hero-plate img {
  width: 100%; height: 100%; object-fit: cover;
  will-change: transform;
}
/* Dinlenme hâli: kare tamamen renksiz */
.hero-plate--base img {
  filter: grayscale(1) contrast(1.12) brightness(0.58);
  transition: transform var(--dur-slow) var(--ease);
}

/* Aynı kare renkli; yalnızca imlecin çevresindeki daire içinde görünür.

   Kutu artık sahne kadar değil, merceğin kendisi kadar. Maske kutunun
   ortasında SABİT duruyor; her karede yalnızca iki dönüşüm kayıyor ve
   hiçbir şey yeniden boyanmıyor. Eskiden maskenin merkezi doğrudan
   mask-image içindeydi (`at var(--lens-x) var(--lens-y)`), yani maske her
   kımıldadığında tam ekran plaka yeniden boyanıyordu.

   Ölçüldü 2026-09-20, 2524x1325 yüzeyde: maske hareketliyken 24.3 ms/kare
   (41 Hz), maske kapalıyken 6.1 ms (164 Hz) — yani bütün maliyet buradaydı.
   Grain katmanı tek başına bedava; pahalı olmasının sebebi, her maske
   boyamasının altındaki overlay karışımını da yeniden hesaplatmasıydı. */
.hero-plate--reveal {
  inset: auto; top: 0; left: 0;
  width: calc(var(--lens-r, 0px) * 2);
  height: calc(var(--lens-r, 0px) * 2);
  background: none;
  opacity: 0;
  overflow: hidden;
  transform: translate3d(calc(var(--lens-x, 0px) - var(--lens-r, 0px)),
                         calc(var(--lens-y, 0px) - var(--lens-r, 0px)), 0);
  transition: opacity var(--dur) var(--ease);
  -webkit-mask-image: radial-gradient(circle var(--lens-r, 0px) at 50% 50%,
                        #000 52%, rgba(0,0,0,0.55) 76%, transparent 100%);
          mask-image: radial-gradient(circle var(--lens-r, 0px) at 50% 50%,
                        #000 52%, rgba(0,0,0,0.55) 76%, transparent 100%);
}
/* Kare kutunun tersine kayıyor: kutu imleci takip ederken fotoğraf sahnede
   çakılı duruyor. Boyu sahnenin boyu olmak zorunda — kutunun %100'ü artık
   yalnızca 2r eder. --stage-w/h'yi main.js koyar ve pencere boyu değişince
   tazeler. */
.hero-plate--reveal img {
  /* max-width:none şart: global sıfırlamadaki `img { max-width: 100% }`
     (style.css:125) kareyi kutunun %100'üne, yani 2r'ye kırpıyor. Plaka
     sahne boyundayken zararsızdı; kutu küçülünce fotoğraf 300 px'e indi ve
     mercek bomboş çıktı. */
  max-width: none;
  width: var(--stage-w, 100vw);
  height: var(--stage-h, 100svh);
  transform: translate3d(calc(var(--lens-r, 0px) - var(--lens-x, 0px)),
                         calc(var(--lens-r, 0px) - var(--lens-y, 0px)), 0);
  filter: grayscale(0) contrast(1.02) saturate(1.06) brightness(0.92);
}
.hero[data-lens-active] .hero-plate--reveal { opacity: 1; }

/* Objektif halkası */
.hero-reticle {
  position: absolute; top: 0; left: 0;
  width: 168px; height: 168px; margin: -84px 0 0 -84px;
  border: 1px solid rgba(244, 242, 239, 0.34);
  border-radius: 50%;
  opacity: 0; pointer-events: none;
  transform: translate3d(var(--lens-px, 0px), var(--lens-py, 0px), 0) scale(0.6);
  transition: opacity var(--dur) var(--ease), transform var(--dur) var(--ease);
}
.hero[data-lens-active] .hero-reticle {
  opacity: 1;
  transform: translate3d(var(--lens-px, 0px), var(--lens-py, 0px), 0) scale(1);
}

/* Karartma katmanı iki plakanın da üstünde: daire içinde de metin okunur kalır */
.hero-scrim {
  position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(180deg, rgba(6,6,6,0.55) 0%, rgba(6,6,6,0.25) 40%, rgba(6,6,6,0.94) 100%);
}

.hero-grain {
  position: absolute; inset: 0; pointer-events: none; opacity: 0.16;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E");
  mix-blend-mode: overlay;
}
.hero-vignette {
  position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(120% 90% at 50% 40%, transparent 45%, rgba(0,0,0,0.7) 100%);
}

.hero-content { position: relative; }

.eyebrow { color: var(--accent); margin-bottom: var(--sp-6); }

.hero-title {
  font-family: var(--font-display);
  font-size: var(--step-4); font-weight: 900;
  line-height: 0.86; letter-spacing: -0.035em;
  margin-bottom: var(--sp-8);
}
.hero-line { display: block; will-change: transform; }
.hero-line--indent { padding-left: clamp(1rem, 8vw, 8rem); }
.hero-amp {
  font-family: var(--font-display);
  font-style: italic; font-weight: 400;
  font-size: 0.42em; color: var(--accent);
  margin-left: 0.2em; vertical-align: 0.35em;
}

.swap { position: relative; display: inline-block; overflow: hidden; vertical-align: bottom; }
.swap-a, .swap-b { display: block; transition: transform var(--dur-slow) var(--ease), opacity var(--dur) var(--ease); }
.swap-b {
  position: absolute; inset: 0;
  font-style: italic; font-weight: 400; color: var(--accent);
  transform: translateY(100%); opacity: 0;
}
.hero-line:hover .swap-a { transform: translateY(-100%); opacity: 0; }
.hero-line:hover .swap-b { transform: translateY(0); opacity: 1; }

.hero-lede {
  /* 300 değil 400: bu paragraf fotoğrafın üstünde ve soluk gri. İnce çizgiler
     düşük DPI'lı ekranda kırılıyordu — sayfanın en kırılgan metni buydu. */
  max-width: 46ch; font-size: var(--step-15); font-weight: 400;
  line-height: 1.5;
  color: var(--fg-lede); margin-bottom: var(--sp-8);
}
.hero-actions { display: flex; flex-wrap: wrap; gap: var(--sp-4); margin-bottom: var(--sp-8); }
.hero-hint {
  display: inline-flex; align-items: center; gap: var(--sp-3);
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.1em; color: var(--fg-dim);
}
.hero-hint svg { width: 16px; height: 16px; fill: none; stroke: var(--accent); stroke-width: 1.2; }

/* ── Buttons ──────────────────────────────────────────────── */
.btn {
  position: relative;
  /* Kendi yığın bağlamı: ::before dolgusu bölüm arkaplanının arkasına kaçmasın */
  isolation: isolate;
  display: inline-flex; align-items: center; justify-content: center; gap: var(--sp-3);
  min-height: 48px; padding: 0 var(--sp-8);
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.18em; text-transform: uppercase;
  border: 1px solid var(--line-strong); background: none; color: var(--fg);
  cursor: pointer; overflow: hidden;
  transition: color var(--dur) var(--ease), border-color var(--dur) var(--ease);
}
.btn::before {
  content: ""; position: absolute; inset: 0; z-index: -1;
  background: var(--fg);
  transform: translateY(101%);
  transition: transform var(--dur) var(--ease);
}
.btn--primary { border-color: var(--fg); }
.btn:hover, .btn:focus-visible { color: var(--bg); border-color: var(--fg); }
.btn:hover::before, .btn:focus-visible::before { transform: translateY(0); }
.btn:active { transform: scale(0.985); }
.btn svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.4; }
.btn--ghost { color: var(--fg-muted); }
.btn--block { width: 100%; }
.btn[disabled] { opacity: 0.45; cursor: not-allowed; }
.btn[disabled]:hover { color: var(--fg); }
.btn[disabled]:hover::before { transform: translateY(101%); }

.spinner { display: none; animation: spin 0.9s linear infinite; }
.spinner circle { stroke-dasharray: 44; stroke-dashoffset: 32; }
.btn[data-loading] .spinner { display: block; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Sections ─────────────────────────────────────────────── */
.section {
  position: relative;
  padding-block: clamp(4.5rem, 11vw, 11rem);
  border-top: 1px solid var(--line);
}
.section--alt { background: var(--bg-elev); }

.section-head { max-width: 62ch; margin-bottom: var(--sp-12); }
.section-index { color: var(--accent); margin-bottom: var(--sp-4); }
.section-title {
  font-family: var(--font-display);
  font-size: var(--step-3); font-weight: 700; line-height: 1.02;
  letter-spacing: -0.025em; margin-bottom: var(--sp-6);
}
.section-title em { font-style: italic; font-weight: 400; color: var(--accent); }
.section-lede {
  color: var(--fg-muted);
  font-size: var(--step-1);
  font-weight: 400;
  line-height: 1.55;
  max-width: 48ch;
}

.disclosure {
  display: flex; gap: var(--sp-3); align-items: flex-start;
  margin-top: var(--sp-6); padding: var(--sp-4);
  border-left: 2px solid var(--accent); background: var(--accent-soft);
  font-size: var(--step--1); color: var(--fg-muted); max-width: 56ch;
}
.disclosure svg {
  flex: none; width: 18px; height: 18px; margin-top: 1px;
  fill: none; stroke: var(--accent); stroke-width: 1.4; stroke-linecap: round;
}

/* ── Coverflow: Work fotoğraf kütüphanesi ─────────────────── */
.coverflow {
  /* JS bu üçünü okur; hepsi SADE SAYI olmalı.
     clamp()/calc() yazılırsa parseFloat NaN döner ve JS sessizce yedeğe düşer. */
  --cf-gap-factor: 0.88;   /* adım = kart genişliği × bu kat sayı */
  --cf-angle: 34;          /* derece */
  --cf-depth: 150;         /* px */

  /* Kart ölçüsü iki eksene birden bağlı. Yalnızca genişliğe bağlıyken
     dar-uzun ekranda (tablet dikey) sahnenin yarısı boş kalıyor, geniş-basık
     ekranda (1280×720 dizüstü) kart sahneden taşıp üstten alttan kırpılıyordu.
     Sahne yüksekliği de buradan türer; ikisi hiçbir ölçüde ayrışamaz. */
  --cf-card-w: clamp(205px, min(26vw, 38vh), 460px);
  position: relative;
}

.coverflow-stage {
  position: relative;
  /* 3/4 kart + gölge payı */
  height: calc(var(--cf-card-w) * 4 / 3 + 3rem);
  perspective: 1600px;
  overflow: hidden;
}

.coverflow-track {
  position: relative; height: 100%;
  transform-style: preserve-3d;
  touch-action: pan-y;   /* dikey sayfa kaydırma korunur */
}

.coverflow-item {
  position: absolute; inset: 0; margin: auto;
  width: var(--cf-card-w);
  height: fit-content;
  aspect-ratio: 3 / 4;
  will-change: transform, opacity;
  transition: transform var(--dur-slow) var(--ease),
              opacity var(--dur) var(--ease);
}
.coverflow-item img {
  display: block; width: 100%; height: 100%; object-fit: cover;
  background: linear-gradient(140deg, var(--bg-elev-2), var(--bg-elev));
  filter: var(--img-grade);
  transition: filter var(--dur-slow) var(--ease);
}
/* Merkezdeki kare renklenir — hero'daki dille aynı */
.coverflow-item[data-active] img { filter: var(--img-grade-hover); }
/* Yan kareler de imleç üzerlerine gelince kendi renklerini açar */
@media (hover: hover) and (pointer: fine) {
  .coverflow-item:hover img { filter: var(--img-grade-hover); }
  .coverflow-item:hover { opacity: 1 !important; }   /* satır içi opaklığı geçer */
}
.coverflow-item[data-active] { box-shadow: 0 30px 70px rgba(0, 0, 0, 0.45); }
/* Merkezdeki kare büyütmeyi açar, yandakiler o yöne bir adım ilerletir */
.coverflow-item { cursor: pointer; }
.coverflow-item[data-active] { cursor: zoom-in; }

.coverflow-nav {
  position: absolute; top: 50%; z-index: 20;
  display: inline-flex; align-items: center; justify-content: center;
  width: 48px; height: 48px; margin-top: -24px;
  background: var(--bg); border: 1px solid var(--line-strong); color: var(--fg);
  cursor: pointer;
  transition: color var(--dur) var(--ease), background var(--dur) var(--ease),
              border-color var(--dur) var(--ease), opacity var(--dur) var(--ease);
}
.coverflow-nav svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.4; }
.coverflow-nav:hover:not([disabled]) { background: var(--fg); color: var(--bg); border-color: var(--fg); }
.coverflow-nav[disabled] { opacity: 0.38; cursor: not-allowed; }
.coverflow-nav--prev { left: 0; }
.coverflow-nav--next { right: 0; }

.coverflow-dots {
  display: flex; justify-content: center; gap: var(--sp-2);
  margin-top: var(--sp-6);
}
.coverflow-dots button {
  width: 44px; height: 44px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: none; border: 0; cursor: pointer;
}
.coverflow-dots button::before {
  content: ""; width: 28px; height: 1px;
  background: var(--line-strong);
  transition: background var(--dur) var(--ease), height var(--dur) var(--ease);
}
.coverflow-dots button[aria-current="true"]::before { background: var(--accent); height: 2px; }
.coverflow-dots button:hover::before { background: var(--fg); }

/* ── Büyütme katmanı ──────────────────────────────────────── */
.lightbox {
  position: fixed; inset: 0; z-index: var(--z-lightbox);
  display: grid; place-items: center;
  padding: clamp(1.25rem, 5vw, 4rem);
  background: rgba(6, 6, 6, 0.94);
  backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
  opacity: 0;
  transition: opacity var(--dur) var(--ease);
  cursor: zoom-out;
}
.lightbox[hidden] { display: none; }
.lightbox[data-open] { opacity: 1; }

.lightbox-figure {
  margin: 0; max-width: 100%; max-height: 100%; min-height: 0;
  display: block; line-height: 0; text-align: center; cursor: default;
}
.lightbox img {
  max-width: 100%; max-height: min(85vh, calc(100dvh - 7rem));
  width: auto; height: auto; object-fit: contain;
  background: #0c0c0c;
  transform: scale(0.94);
  transition: transform var(--dur-slow) var(--ease);
}
.lightbox[data-open] img { transform: none; }

.lightbox-close {
  position: absolute; top: clamp(1rem, 3vw, 2rem); right: clamp(1rem, 3vw, 2rem);
  display: inline-flex; align-items: center; justify-content: center;
  width: 48px; height: 48px;
  background: none; border: 1px solid rgba(244, 242, 239, 0.3);
  color: #f4f2ef; cursor: pointer;
  transition: background var(--dur) var(--ease), border-color var(--dur) var(--ease),
              color var(--dur) var(--ease);
}
.lightbox-close:hover { background: #f4f2ef; border-color: #f4f2ef; color: #060606; }
.lightbox-close svg {
  width: 20px; height: 20px;
  fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round;
}

.lightbox-nav {
  position: absolute; top: 50%; z-index: 2;
  display: inline-flex; align-items: center; justify-content: center;
  width: 56px; height: 56px; margin-top: -28px;
  background: rgba(6, 6, 6, 0.55);
  border: 1px solid rgba(244, 242, 239, 0.3);
  color: #f4f2ef; cursor: pointer;
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  transition: background var(--dur) var(--ease), border-color var(--dur) var(--ease),
              color var(--dur) var(--ease);
}
.lightbox-nav:hover { background: #f4f2ef; border-color: #f4f2ef; color: #060606; }
.lightbox-nav svg {
  width: 22px; height: 22px;
  fill: none; stroke: currentColor; stroke-width: 1.5;
  stroke-linecap: round; stroke-linejoin: round;
}
.lightbox-nav--prev { left: clamp(0.5rem, 2.5vw, 2.5rem); }
.lightbox-nav--next { right: clamp(0.5rem, 2.5vw, 2.5rem); }

.lightbox-count {
  position: absolute; left: 0; right: 0;
  bottom: clamp(1rem, 3vw, 2rem);
  text-align: center;
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.2em; font-variant-numeric: tabular-nums;
  color: rgba(244, 242, 239, 0.6);
}

/* Albüm şeridi: açık karenin altında serinin diğer kareleri */
.lightbox-strip {
  position: absolute; left: 0; right: 0; bottom: clamp(1rem, 3vw, 1.75rem);
  z-index: 3;
  display: flex;
  padding: 0 clamp(1rem, 4vw, 3.5rem);
  overflow-x: auto; overscroll-behavior-x: contain;
  scrollbar-width: none; cursor: default;
}
.lightbox-strip::-webkit-scrollbar { display: none; }
.lightbox:not([data-has-strip]) .lightbox-strip { display: none; }
.lightbox-strip-inner { display: flex; gap: 0.5rem; margin-inline: auto; }

.lightbox-thumb {
  flex: 0 0 auto;
  width: 54px; height: 72px; padding: 0;
  background: none; border: 1px solid transparent;
  opacity: 0.42; cursor: pointer;
  transition: opacity var(--dur) var(--ease), border-color var(--dur) var(--ease);
}
.lightbox-thumb img {
  width: 100%; height: 100%; max-height: none;
  object-fit: cover; display: block;
  transform: none; transition: none;
}
.lightbox-thumb:hover { opacity: 0.8; }
.lightbox-thumb:focus-visible { outline: 2px solid #f4f2ef; outline-offset: 2px; }
.lightbox-thumb[aria-current="true"] { opacity: 1; border-color: #f4f2ef; }

.lightbox[data-has-strip] { padding-bottom: calc(72px + clamp(4rem, 9vw, 5.5rem)); }
.lightbox[data-has-strip] img { max-height: min(70vh, calc(100dvh - 13.5rem)); }
.lightbox[data-has-strip] .lightbox-count { bottom: calc(72px + clamp(2.4rem, 5vw, 3rem)); }

/* Dar ekranda oklar fotoğrafın üstünü kapatıyordu: alta, sayacın yanına alınır */
@media (max-width: 720px) {
  .lightbox { padding-bottom: 6rem; }
  .lightbox img { max-height: 68vh; }

  .lightbox-nav {
    top: auto; bottom: clamp(1rem, 4vw, 2rem); margin-top: 0;
    width: 48px; height: 48px;
  }
  .lightbox-nav--prev { left: 50%; margin-left: -84px; }
  .lightbox-nav--next { right: 50%; margin-right: -84px; }

  .lightbox-count {
    bottom: clamp(1rem, 4vw, 2rem);
    height: 48px; display: flex; align-items: center; justify-content: center;
  }

  .lightbox-thumb { width: 42px; height: 56px; }
  .lightbox-strip { bottom: 1rem; padding: 0 1rem; }
  .lightbox[data-has-strip] { padding-bottom: 13rem; }
  .lightbox[data-has-strip] img { max-height: min(52vh, calc(100dvh - 17rem)); }
  .lightbox[data-has-strip] .lightbox-nav { bottom: 8rem; }
  .lightbox[data-has-strip] .lightbox-count { bottom: 4.6rem; }
}

/* ── Kart ızgarası (Digital) ──────────────────────────────── */
.grid {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: clamp(1rem, 2.4vw, 2.25rem); align-items: start;
}
.card[hidden] { display: none; }
.card a { display: grid; gap: var(--sp-3); }
/* Üretken görsel kartları gezinmez, büyütür — imleç bunu söylesin */
.card a[data-digital-hit] { cursor: zoom-in; }

.card-media {
  position: relative; display: block; overflow: hidden;
  background: linear-gradient(140deg, var(--bg-elev-2), var(--bg-elev));
  aspect-ratio: 3 / 4;
}
.card-media img {
  width: 100%; height: 100%; object-fit: cover;
  filter: var(--img-grade);
  transform: scale(1.02);
  transition: transform var(--dur-slow) var(--ease), filter var(--dur-slow) var(--ease);
}
.card-media::after {
  content: ""; position: absolute; inset: 0;
  border: 1px solid transparent;
  transition: border-color var(--dur) var(--ease);
}
.card a:hover .card-media img,
.card a:focus-visible .card-media img { transform: scale(1.07); filter: var(--img-grade-hover); }
.card a:hover .card-media::after,
.card a:focus-visible .card-media::after { border-color: var(--accent); }

.ai-badge {
  position: absolute; top: var(--sp-3); left: var(--sp-3);
  padding: 4px var(--sp-2);
  background: rgba(6, 6, 6, 0.72); color: #f4f2ef;
  border: 1px solid rgba(244, 242, 239, 0.32);
  font-family: var(--font-mono); font-size: 0.6rem;
  letter-spacing: 0.22em; text-transform: uppercase;
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
}

.card-meta { display: flex; justify-content: space-between; color: var(--fg-dim); }
.card-cat { color: var(--accent); }
.card-title {
  font-family: var(--font-display);
  font-size: var(--step-2); font-weight: 500; line-height: 1.1;
  letter-spacing: -0.015em;
}
.card-desc {
  color: var(--fg-muted);
  font-size: var(--step-05); font-weight: 400;
  line-height: 1.5; letter-spacing: 0.01em;
}

/* ── Videografi: genişleyen panel şeridi ──────────────────────
   Dinlenme hâli: dört panel eşit genişlikte. Bir panele gelince o
   panel genişler ve açıklaması açılır, diğerleri dikey şeride iner.
   Tıklama paneli kilitler ve videoyu oynatır. */
.reel {
  display: flex; gap: 2px;
  height: clamp(430px, 68vh, 700px);
  background: var(--line);
  border: 1px solid var(--line);
}

.reel-panel {
  position: relative;
  flex: 1 1 0; min-width: 0;
  display: flex; flex-direction: column; justify-content: space-between;
  padding: clamp(0.9rem, 1.6vw, 1.6rem);
  overflow: hidden;
  transition: flex-grow var(--dur-slow) var(--ease);
}
/* Şablondaki beyaz / siyah / vurgu ritmi, sitenin kendi tokenleriyle */
.reel-panel:nth-child(1) { background: var(--fg); color: var(--bg); }
.reel-panel:nth-child(2) { background: var(--bg); color: var(--fg); }
.reel-panel:nth-child(3) { background: var(--accent); color: #0b0b0a; }
.reel-panel:nth-child(4) { background: var(--bg-elev-2); color: var(--fg); }

.reel[data-has-active] .reel-panel { flex-grow: 0.42; }
.reel[data-has-active] .reel-panel[data-active] { flex-grow: 3.6; }

/* Kapalı sütunda kareden alınan sabit görsel.
   object-position her panelde ayrı ayarlanır (--fx/--fy): dar şeritte
   görselin yalnızca ~%12'si görünür, bu yüzden asıl özne oraya denk gelmeli. */
.reel-still {
  position: absolute; inset: 0; z-index: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  object-position: var(--fx, 50%) var(--fy, 50%);
  filter: var(--img-grade);
  transition: opacity var(--dur) var(--ease), filter var(--dur-slow) var(--ease);
}
/* Panel açılınca görsel çekilir, panelin kendi rengi ve videosu öne gelir */
.reel-panel[data-active] .reel-still { opacity: 0; }
.reel-panel:not([data-active]):hover .reel-still { filter: var(--img-grade-hover); }

/* Numara ve başlığın görselin üstünde okunması için perde */
.reel-panel::before {
  content: ""; position: absolute; inset: 0; z-index: 1;
  background: linear-gradient(180deg,
    rgba(6, 6, 6, 0.78) 0%, rgba(6, 6, 6, 0.28) 30%,
    rgba(6, 6, 6, 0.34) 62%, rgba(6, 6, 6, 0.88) 100%);
  transition: opacity var(--dur) var(--ease);
  pointer-events: none;
}
.reel-panel[data-active]::before { opacity: 0; }

/* Panelin tamamı tıklama hedefi; içerik üstte kalır */
.reel-hit {
  position: absolute; inset: 0; z-index: 3;
  background: none; border: 0; padding: 0;
  cursor: pointer; color: inherit;
}
.reel-hit:focus-visible { outline: 2px solid currentColor; outline-offset: -6px; }

/* Kapalıyken metin görselin üstünde: her panelde açık renk.
   nth-child renk kurallarından SONRA gelmeli (aynı özgüllük, son yazan kazanır). */
.reel-panel:not([data-active]) { color: #f4f2ef; }

.reel-no {
  position: relative; z-index: 2;
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.2em; opacity: 0.85;
  flex: none;
}

/* Açılan içerik: dar hâlde tamamen kapalı */
.reel-open {
  position: relative; z-index: 2;
  flex: 1 1 auto; min-height: 0;
  display: flex; flex-direction: column; gap: var(--sp-4);
  opacity: 0; visibility: hidden;
  transform: translateY(10px);
  transition: opacity var(--dur) var(--ease), transform var(--dur) var(--ease),
              visibility 0s linear var(--dur);
  padding-block: var(--sp-4);
}
.reel-panel[data-active] .reel-open {
  opacity: 1; visibility: visible; transform: none;
  transition-delay: 140ms, 140ms, 0s;   /* panel genişledikten sonra açılsın */
}

.reel-spec {
  flex: none;
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.14em; text-transform: uppercase;
  opacity: 0.75;
}

.reel-stage {
  position: relative; flex: 1 1 auto; min-height: 0;
  overflow: hidden; background: #0b0b0a;
}
.reel-stage video {
  width: 100%; height: 100%; object-fit: cover; display: block;
  filter: var(--img-grade);
  transition: filter var(--dur-slow) var(--ease);
}
.reel-panel[data-active] .reel-stage video { filter: var(--img-grade-hover); }

.reel-play {
  position: absolute; top: 50%; left: 50%;
  display: grid; place-items: center;
  width: 68px; height: 68px; margin: -34px 0 0 -34px;
  border: 1px solid rgba(244, 242, 239, 0.55); border-radius: 50%;
  background: rgba(6, 6, 6, 0.42);
  backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
  transition: background var(--dur) var(--ease), transform var(--dur) var(--ease),
              border-color var(--dur) var(--ease), opacity var(--dur) var(--ease);
}
.reel-play svg { width: 26px; height: 26px; fill: #f4f2ef; }
.reel-panel:hover .reel-play {
  background: rgba(201, 178, 138, 0.9); border-color: var(--accent); transform: scale(1.08);
}
.reel-panel:hover .reel-play svg { fill: #060606; }
/* Oynarken düğme çekilir */
.reel-panel[data-playing] .reel-play { opacity: 0; transform: scale(0.8); }

.reel-time {
  position: absolute; right: var(--sp-3); bottom: var(--sp-3);
  padding: 3px var(--sp-2);
  background: rgba(6, 6, 6, 0.72); color: #f4f2ef;
  font-family: var(--font-mono); font-size: 0.62rem; letter-spacing: 0.14em;
  font-variant-numeric: tabular-nums;
}

.reel-note {
  flex: none; max-width: 46ch;
  font-size: var(--step-0); line-height: 1.55; opacity: 0.78;
}

/* Başlık: dar hâlde dikey, açık hâlde altta iri */
.reel-title {
  position: relative; z-index: 2;
  flex: none;
  font-family: var(--font-display); font-weight: 700;
  letter-spacing: -0.02em; line-height: 0.9;
  text-transform: uppercase; white-space: nowrap;
  writing-mode: vertical-rl; transform: rotate(180deg);
  font-size: var(--step-1);
  transition: font-size var(--dur-slow) var(--ease);
}
.reel-panel[data-active] .reel-title {
  writing-mode: horizontal-tb; transform: none;
  font-size: clamp(2rem, 5.5vw, 5rem);
}

@media (max-width: 900px) {
  /* Dar ekranda yatay şerit çalışmaz: dikey yığına döner */
  .reel { flex-direction: column; height: auto; }
  .reel-panel { flex: none; }
  .reel[data-has-active] .reel-panel,
  .reel[data-has-active] .reel-panel[data-active] { flex-grow: 0; }
  .reel-title { writing-mode: horizontal-tb; transform: none; font-size: var(--step-2); }
  .reel-panel[data-active] .reel-title { font-size: var(--step-3); }
  .reel-open { display: none; }
  .reel-panel[data-active] .reel-open { display: flex; }
  .reel-stage { aspect-ratio: 16 / 9; flex: none; }
}

/* ── Studio ───────────────────────────────────────────────── */
.studio-split {
  display: grid; grid-template-columns: 5fr 6fr;
  gap: clamp(2rem, 6vw, 6rem); align-items: start;
}
.studio-figure { position: sticky; top: 7rem; }
.studio-figure img {
  width: 100%; aspect-ratio: 4 / 5; object-fit: cover;
  filter: var(--img-grade); background: var(--bg-elev-2);
}
.studio-figure figcaption {
  margin-top: var(--sp-4);
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.12em; color: var(--fg-dim);
}
.studio-body { display: grid; gap: var(--sp-6); max-width: 60ch; }
.studio-body p { font-size: var(--step-1); line-height: 1.6; color: var(--fg-muted); }
.studio-body .lede {
  font-size: var(--step-175); font-weight: 300; line-height: 1.45; color: var(--fg);
}

.stats {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: var(--sp-4); margin-top: var(--sp-4);
  padding-block: var(--sp-6); border-block: 1px solid var(--line);
}
.stats dt {
  font-family: var(--font-mono); font-size: 0.66rem;
  letter-spacing: 0.2em; text-transform: uppercase; color: var(--fg-dim);
  margin-bottom: var(--sp-2);
}
.stats dd {
  font-family: var(--font-display);
  font-size: var(--step-2); font-weight: 500; font-variant-numeric: tabular-nums;
}

.studio-cta { display: grid; gap: var(--sp-4); justify-items: start; }
.studio-cta p { color: var(--fg-muted); }

/* ── Services ─────────────────────────────────────────────── */
.services { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1px; background: var(--line); }
.service {
  background: var(--bg);
  padding: clamp(1.75rem, 4vw, 3rem);
  display: grid; gap: var(--sp-3); align-content: start;
  transition: background var(--dur) var(--ease);
}
.service:hover { background: var(--bg-elev); }
.service-no { color: var(--accent); }
.service-title { font-family: var(--font-display); font-size: var(--step-2); font-weight: 500; letter-spacing: -0.015em; }
.service-desc {
  color: var(--fg-muted);
  font-size: var(--step-05); font-weight: 400;
  line-height: 1.55; max-width: 40ch;
}
.service-meta { color: var(--fg-dim); margin-top: var(--sp-2); }

/* ── Contact ──────────────────────────────────────────────── */
.contact-split { display: grid; grid-template-columns: 7fr 5fr; gap: clamp(2rem, 6vw, 6rem); }

.form { display: grid; gap: var(--sp-6); }
.field { display: grid; gap: var(--sp-2); }
.field label {
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.16em; text-transform: uppercase; color: var(--fg-muted);
}
.req { color: var(--accent); }
.field input, .field select, .field textarea {
  min-height: 48px; padding: var(--sp-3) var(--sp-4);
  background: var(--bg); border: 1px solid var(--line);
  transition: border-color var(--dur) var(--ease), background var(--dur) var(--ease);
}
.field textarea { min-height: 8rem; resize: vertical; line-height: 1.6; }
.field select { appearance: none; cursor: pointer; }
.field select option { background: var(--bg); color: var(--fg); }
.field input:hover, .field select:hover, .field textarea:hover { border-color: var(--line-strong); }
.field input:focus, .field select:focus, .field textarea:focus {
  border-color: var(--accent); background: var(--bg-elev-2); outline: none;
}
.field-help { font-size: var(--step-0); color: var(--fg-dim); line-height: 1.5; }
.field-err { font-size: var(--step-0); color: var(--danger); display: flex; gap: var(--sp-2); }
.field-err[hidden] { display: none; }
.field-err::before { content: "!"; font-family: var(--font-mono); font-weight: 500; }
.field[data-invalid] input, .field[data-invalid] textarea { border-color: var(--danger); }

.form-status:empty { display: none; }
.form-status {
  padding: var(--sp-3) var(--sp-4);
  border-left: 2px solid var(--success); background: var(--bg);
  font-size: var(--step--1); color: var(--fg-muted);
}
.form-status[data-error] { border-left-color: var(--danger); }

.contact-info dl { display: grid; gap: var(--sp-6); }
.contact-info dt {
  font-family: var(--font-mono); font-size: 0.66rem;
  letter-spacing: 0.2em; text-transform: uppercase; color: var(--accent);
  margin-bottom: var(--sp-2);
}
.contact-info dd {
  color: var(--fg-muted);
  font-size: var(--step-05); font-weight: 400; line-height: 1.55;
}
.contact-info a {
  border-bottom: 1px solid var(--line-strong); padding-bottom: 1px;
  transition: border-color var(--dur) var(--ease), color var(--dur) var(--ease);
}
.contact-info a:hover { color: var(--accent); border-color: var(--accent); }
.contact-note {
  margin-top: var(--sp-8); padding-top: var(--sp-6);
  border-top: 1px solid var(--line);
  font-family: var(--font-mono); font-size: var(--step-0);
  letter-spacing: 0.1em; color: var(--fg-dim);
}

/* ── Footer ───────────────────────────────────────────────── */
.site-footer { border-top: 1px solid var(--line); padding-block: var(--sp-12); }
.footer-inner {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
  gap: var(--sp-6);
}
.footer-mark { font-family: var(--font-display); font-size: var(--step-2); font-weight: 700; letter-spacing: 0.06em; }
.footer-links { display: flex; flex-wrap: wrap; gap: var(--sp-6); }
.footer-links a {
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.16em; text-transform: uppercase; color: var(--fg-muted);
  min-height: 44px; display: inline-flex; align-items: center;
  transition: color var(--dur) var(--ease);
}
.footer-links a:hover { color: var(--accent); }
.footer-legal { color: var(--fg-dim); letter-spacing: 0.1em; }

/* ── Alt sayfalar: günlük + yazı ──────────────────────────── */
.page { padding-block: clamp(7rem, 12vw, 10rem) clamp(4rem, 8vw, 7rem); }

.crumbs ol { display: flex; flex-wrap: wrap; gap: var(--sp-3); }
.crumbs li {
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--fg-dim);
}
.crumbs li + li::before { content: "/"; margin-right: var(--sp-3); color: var(--fg-dim); }
.crumbs a { color: var(--fg-muted); transition: color var(--dur) var(--ease); }
.crumbs a:hover { color: var(--accent); }

.page-head { max-width: 62ch; margin: var(--sp-8) 0 var(--sp-16); }
.page-title {
  font-family: var(--font-display);
  font-size: var(--step-3); font-weight: 700; line-height: 1.02;
  letter-spacing: -0.025em; margin-bottom: var(--sp-6);
}
.page-title em { font-style: italic; font-weight: 400; color: var(--accent); }

/* Yalnızca İngilizce seçiliyken görünür uyarı */
.lang-note { display: none; }
:root[data-lang="en"] .lang-note {
  display: flex; gap: var(--sp-3); align-items: flex-start;
  margin-top: var(--sp-6); padding: var(--sp-4);
  border-left: 2px solid var(--accent); background: var(--accent-soft);
  font-size: var(--step--1); color: var(--fg-muted); max-width: 56ch;
}
.lang-note svg {
  flex: none; width: 18px; height: 18px; margin-top: 1px;
  fill: none; stroke: var(--accent); stroke-width: 1.4; stroke-linecap: round;
}

.posts { display: grid; gap: var(--sp-12); }
.post a {
  display: grid; grid-template-columns: 5fr 7fr;
  gap: clamp(1.5rem, 4vw, 3rem); align-items: center;
}
.post-media { display: block; overflow: hidden; background: var(--bg-elev-2); aspect-ratio: 3 / 2; }
.post-media img {
  width: 100%; height: 100%; object-fit: cover;
  filter: var(--img-grade); transform: scale(1.02);
  transition: transform var(--dur-slow) var(--ease), filter var(--dur-slow) var(--ease);
}
.post a:hover .post-media img,
.post a:focus-visible .post-media img { transform: scale(1.06); filter: var(--img-grade-hover); }

.post-meta {
  display: flex; flex-wrap: wrap; align-items: center; gap: var(--sp-4);
  margin-bottom: var(--sp-3);
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.16em; text-transform: uppercase; color: var(--fg-dim);
}
.post-title {
  font-family: var(--font-display);
  font-size: var(--step-2); font-weight: 500; line-height: 1.12;
  letter-spacing: -0.015em; margin-bottom: var(--sp-3);
  transition: color var(--dur) var(--ease);
}
.post a:hover .post-title, .post a:focus-visible .post-title { color: var(--accent); }
.post-excerpt {
  color: var(--fg-muted);
  font-size: var(--step-05); font-weight: 400; line-height: 1.55; max-width: 54ch;
}

/* Yazı gövdesi */
.article { max-width: 68ch; margin-inline: auto; }
.article-head { margin: var(--sp-8) 0 var(--sp-12); }
.article-lede {
  font-size: var(--step-175); font-weight: 300; line-height: 1.45;
  color: var(--fg-muted);
}
.article-figure { margin-bottom: var(--sp-12); }
.article-figure img { width: 100%; aspect-ratio: 3 / 2; object-fit: cover; filter: var(--img-grade); background: var(--bg-elev-2); }
.article-figure figcaption {
  margin-top: var(--sp-3);
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.1em; color: var(--fg-dim);
}
.article h2 {
  font-family: var(--font-display);
  font-size: var(--step-2); font-weight: 500; line-height: 1.15;
  letter-spacing: -0.015em;
  margin: var(--sp-12) 0 var(--sp-4);
}
/* Uzun metin gövdesi genel --step-0'dan bir kademe yukarıda:
   68ch'lik sütunda 0.95rem satır uzunluğunu okunmaz kılıyordu.
   Lede dışarıda: .article p (0-1-1) .article-lede'i (0-1-0) yener,
   :not() olmadan giriş paragrafı gövdeye eşitleniyordu. */
.article p:not(.article-lede),
.article-list li { font-size: var(--step-1); line-height: 1.6; }
.article p { color: var(--fg-muted); margin-bottom: var(--sp-4); }
.article strong { color: var(--fg); font-weight: 500; }
.article-list { display: grid; gap: var(--sp-3); margin: 0 0 var(--sp-6); padding-left: var(--sp-6); }
.article-list li { color: var(--fg-muted); position: relative; }
ol.article-list { counter-reset: n; }
ol.article-list li::before {
  counter-increment: n; content: counter(n, decimal-leading-zero);
  position: absolute; left: calc(var(--sp-6) * -1);
  font-family: var(--font-mono); font-size: 0.68rem; color: var(--accent);
}
ul.article-list li::before {
  content: "—"; position: absolute; left: calc(var(--sp-6) * -1); color: var(--accent);
}
.article-quote {
  margin: var(--sp-8) 0;
  padding: var(--sp-6) 0 var(--sp-6) var(--sp-6);
  border-left: 2px solid var(--accent);
  font-family: var(--font-display); font-style: italic;
  font-size: var(--step-175); line-height: 1.4; color: var(--fg);
}

.article-nav {
  display: flex; flex-wrap: wrap; justify-content: space-between; gap: var(--sp-4);
  max-width: 68ch; margin: var(--sp-16) auto 0;
  padding-top: var(--sp-6); border-top: 1px solid var(--line);
}
.article-nav a {
  min-height: 44px; display: inline-flex; align-items: center;
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.14em; text-transform: uppercase; color: var(--fg-muted);
  transition: color var(--dur) var(--ease);
}
.article-nav a:hover { color: var(--accent); }

.page-cta {
  display: grid; gap: var(--sp-4); justify-items: start;
  max-width: 68ch; margin: var(--sp-16) auto 0;
  padding: clamp(1.75rem, 4vw, 3rem);
  border: 1px solid var(--line); background: var(--bg-elev);
}
.page-cta h2 { font-family: var(--font-display); font-size: var(--step-2); font-weight: 500; }
.page-cta p { color: var(--fg-muted); }

@media (max-width: 760px) {
  .post a { grid-template-columns: 1fr; }
}

/* ── Scroll reveal ────────────────────────────────────────── */
.reveal {
  opacity: 0; transform: translate3d(0, 28px, 0);
  transition: opacity var(--dur-slow) var(--ease), transform var(--dur-slow) var(--ease);
  transition-delay: calc(var(--i, 0) * 70ms);
}
.reveal.is-visible { opacity: 1; transform: none; }

/* ── Responsive ───────────────────────────────────────────── */
@media (max-width: 1280px) {
  .tabs { display: none; }
  .menu-toggle { display: inline-flex; }
}

@media (max-width: 1080px) {
  .grid { grid-template-columns: repeat(2, 1fr); }
  .studio-split, .contact-split { grid-template-columns: 1fr; }
  .studio-figure { position: static; }
}

@media (max-width: 860px) {
  .hero { min-height: 92svh; }
  .hero-hint { display: none; }
  .services { grid-template-columns: 1fr; }
}

/* Dar ekranda başlık çubuğu: marka + dil + tema + menü sığmalı */
@media (max-width: 560px) {
  .brand-mark { letter-spacing: 0.14em; }
  .header-tools { gap: var(--sp-2); }
  .lang-switch button { min-width: 34px; padding: 0; }
  .menu-toggle { width: 44px; padding: 0; justify-content: center; }
  /* Etiket görünmez ama düğmenin erişilebilir adı olarak kalır */
  .menu-toggle-label {
    position: absolute; width: 1px; height: 1px;
    overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap;
  }
}

/* 320 px sınıfı ekranlar (küçük telefonlar, %400 yakınlaştırma).
   Marka + dil + tema + menü tek satıra sığmıyordu: menü düğmesi ekranın
   dışına taşıyor, overflow-x:hidden onu tamamen yutuyor ve gezinme
   erişilemez hâle geliyordu. Dokunma hedefleri 44 px kalır; yer
   harf aralığından ve boşluklardan kazanılır. */
@media (max-width: 400px) {
  :root { --gutter: 1rem; }

  .header-inner { gap: var(--sp-2); }
  .header-tools { gap: 6px; flex: none; }

  /* Emniyet: marka ne kadar uzarsa uzasın araçları ekran dışına itemez */
  .brand { min-width: 0; overflow: hidden; }
  .brand-mark { letter-spacing: 0.02em; }
  .brand-sub { letter-spacing: 0.18em; }

  /* Genişlik daralır, 44 px yükseklik (asıl dokunma ölçüsü) korunur */
  .lang-switch button { min-width: 30px; }
}

@media (max-width: 640px) {
  .grid { grid-template-columns: 1fr; }
  /* Küçük ekranda derinlik yorucu görünüyor: açı ve mesafe azaltılır.
     Kart genişliği token üzerinden verilir ki sahne yüksekliği de onunla gelsin. */
  .coverflow { --cf-angle: 24; --cf-depth: 110; --cf-gap-factor: 0.62;
               --cf-card-w: min(70vw, 300px); }
  .coverflow-nav { width: 44px; height: 44px; margin-top: -22px; }
  .stats { grid-template-columns: repeat(2, 1fr); row-gap: var(--sp-6); }
  .footer-inner { flex-direction: column; align-items: flex-start; }
}

/* Yatay tutulan telefon: genişlik var, yükseklik yok.
   Sabit 8rem/4rem hero dolgusu ve 7rem menü dolgusu bu ekranda ölçünün
   yarısını yiyordu; hero 390 px'lik görünüme 693 px olarak oturuyor,
   menünün son maddeleri kaydırmadan görünmüyordu. */
@media (max-height: 520px) and (orientation: landscape) {
  .hero {
    min-height: 0;
    padding-block: 5.5rem var(--sp-8);
  }
  /* Yükseklikle ölçeklenir: üç satırlık başlık görünümü boğmasın */
  .hero-title { font-size: clamp(2rem, 9vh, 3.5rem); margin-bottom: var(--sp-6); }
  .hero-lede { margin-bottom: var(--sp-6); }
  .hero-actions { margin-bottom: 0; }

  .mobile-menu { padding-top: 5.5rem; }
  .mobile-menu-list a { padding-block: var(--sp-2); }
  .mobile-menu-list em { font-size: clamp(1.2rem, 5vh, 1.8rem); }
  .mobile-menu-foot { margin-top: var(--sp-4); }
}

/* Dokunmatik: hover'a bağlı hiçbir efekt yok.
   Hero yalnızca renksiz hâliyle durur; renkli katman hiç boyanmaz. */
@media (hover: none) {
  .hero-plate--reveal, .hero-reticle { display: none; }
  .hero-line:hover .swap-a { transform: none; opacity: 1; }
  .hero-line:hover .swap-b { opacity: 0; }
}

/* ── Reduced motion ───────────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  html { scroll-behavior: auto; }
  *, *::before, *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
  }
  .reveal { opacity: 1; transform: none; }
}

/* İşler bölümü — tüm işleri gör düğmesi */
.work-more {
  display: flex;
  justify-content: center;
  margin-top: clamp(2rem, 4vw, 3.5rem);
}

/* ── Arşiv ızgarası ───────────────────────────────────────
   Her kare kendi en-boy oranı (--r) ve kaynak çözünürlüğü (--s)
   kadar yer kaplar; satırlar kendini hizalar. */
.works-grid {
  list-style: none;
  display: flex; flex-wrap: wrap; align-items: flex-start;
  gap: var(--sp-4);
  margin: clamp(2.5rem, 5vw, 4.5rem) 0 0;
  padding: 0;
}
.works-item {
  position: relative;
  flex: 1 1 calc(var(--r) * var(--s, 1) * 26rem);
  min-width: min(15rem, 100%);
  /* Tek başına kalan kare satırın tamamını yutmasın */
  max-width: min(100%, 30rem);
}
.works-item > a {
  display: block; position: relative;
  /* Kadraj kırpılmasın: kutu karenin kendi oranını alır */
  aspect-ratio: var(--r);
  overflow: hidden;
  background: var(--bg-elev);
}
.works-item img {
  display: block; width: 100%; height: 100%;
  object-fit: cover;
  filter: saturate(0.94);
  transition: transform 720ms var(--ease), filter var(--dur) var(--ease);
}
.works-item > a:hover img,
.works-item > a:focus-visible img { transform: scale(1.028); filter: saturate(1); }
.works-meta {
  position: absolute; inset: auto 0 0 0;
  display: flex; align-items: baseline; justify-content: space-between;
  gap: var(--sp-3);
  padding: var(--sp-4) var(--sp-4) var(--sp-3);
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.16em; text-transform: uppercase;
  color: #f2efea;
  background: linear-gradient(to top, rgba(6, 6, 6, 0.62), rgba(6, 6, 6, 0));
  opacity: 0; transform: translateY(6px);
  transition: opacity var(--dur) var(--ease), transform var(--dur) var(--ease);
}
.works-item > a:hover .works-meta,
.works-item > a:focus-visible .works-meta { opacity: 1; transform: translateY(0); }
.works-year { color: rgba(242, 239, 234, 0.66); }

/* Albüm kapağı: kadraj tek kare değil, seri olduğunu söyler */
.works-album-badge {
  position: absolute; top: var(--sp-4); right: var(--sp-4);
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  font-family: var(--font-mono); font-size: var(--step--1);
  letter-spacing: 0.16em; text-transform: uppercase; line-height: 1;
  color: #f2efea;
  background: rgba(6, 6, 6, 0.48);
  border: 1px solid rgba(242, 239, 234, 0.34);
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  transition: background var(--dur) var(--ease), border-color var(--dur) var(--ease),
              color var(--dur) var(--ease);
}
.works-album-badge svg {
  width: 14px; height: 14px;
  fill: none; stroke: currentColor; stroke-width: 1.4;
  stroke-linecap: round; stroke-linejoin: round;
}
.works-album-count { color: rgba(242, 239, 234, 0.7); }
.works-item--album > a:hover .works-album-badge,
.works-item--album > a:focus-visible .works-album-badge {
  background: #f4f2ef; border-color: #f4f2ef; color: #060606;
}
.works-item--album > a:hover .works-album-count,
.works-item--album > a:focus-visible .works-album-count { color: rgba(6, 6, 6, 0.6); }

@media (max-width: 700px) {
  .works-item { flex-basis: calc(var(--r) * var(--s, 1) * 18rem); max-width: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .works-item img { transition: none; }
  .works-item > a:hover img { transform: none; }
}

/* ══ Panelden eklenen genel bölümler (templates/sections/text|image-text|quote|cta) ══
   Yalnızca mevcut sınıflara yaslanıyor (.section-head, .section-title …);
   buradaki kurallar yerleşimi veriyor, tonu değil. */
.block-body { max-width: 62ch; }
.block-split { display: grid; gap: var(--sp-12); align-items: center; }
@media (min-width: 860px) {
  .block-split { grid-template-columns: 1fr 1fr; }
  .block-image-text[data-side="right"] .block-figure { order: 2; }
}
.block-figure { margin: 0; aspect-ratio: 4 / 5; overflow: hidden; background: var(--bg-elev); }
.block-figure img { width: 100%; height: 100%; object-fit: cover; display: block; filter: var(--img-grade); transition: filter var(--dur-slow) var(--ease); }
.block-figure:hover img { filter: var(--img-grade-hover); }
.block-quote figure { margin: 0; max-width: 28ch; }
.block-quote blockquote { margin: 0 0 var(--sp-6); font-style: italic; }
.block-cta .section-head { margin-bottom: var(--sp-8); }

/* ══ Blok editörü (tools/src/blocks) ═════════════════════════
   Panelden eklenen genel blokların yerleşimi ve zengin metnin boyut
   sınıfları. Ton sitenin kendi değişkenlerinden geliyor. */
.t-xs { font-size: 0.75em; }
.t-sm { font-size: 0.875em; }
.t-lg { font-size: 1.25em; }
.t-xl { font-size: 1.6em; }
.t-2xl { font-size: 2.2em; }

.b-h3 { font-family: var(--font-display); font-size: var(--step-2); font-weight: 400; line-height: 1.15; margin: var(--sp-8) 0 var(--sp-4); }
.b-h4 { font-size: var(--step-1); font-weight: 500; margin: var(--sp-6) 0 var(--sp-3); }
.b-small { font-size: var(--step--1); color: var(--fg-muted); }
.b-list { padding-left: 1.2em; max-width: 62ch; }
.b-list li { margin: 0.35em 0; }
.b-table { overflow-x: auto; margin: var(--sp-8) 0; }
.b-table table { border-collapse: collapse; width: 100%; font-size: var(--step-0); }
.b-table th, .b-table td { text-align: left; padding: 0.6em 0.9em; border-bottom: 1px solid var(--line); vertical-align: top; }
.b-table th { color: var(--fg-muted); font-weight: 500; }
.b-buttons { display: flex; flex-wrap: wrap; gap: var(--sp-4); margin: var(--sp-8) 0; }
.b-divider { border: 0; border-top: 1px solid var(--line); margin: var(--sp-12) 0; }
.b-spacer--s { height: var(--sp-4); }
.b-spacer--m { height: var(--sp-8); }
.b-spacer--l { height: var(--sp-16); }
.b-spacer--xl { height: var(--sp-32); }
.b-image { margin: var(--sp-8) 0; }
.b-image-frame { overflow: hidden; background: var(--bg-elev); }
.b-image-frame img { width: 100%; height: 100%; object-fit: cover; display: block; }
.b-image figcaption { margin-top: var(--sp-3); font-size: var(--step--1); color: var(--fg-muted); }
.b-image--bleed { margin-inline: calc(var(--gutter) * -1); }
.b-gallery { list-style: none; padding: 0; display: grid; gap: var(--sp-4); }
.b-gallery--2 { grid-template-columns: repeat(2, 1fr); }
.b-gallery--3 { grid-template-columns: repeat(3, 1fr); }
.b-gallery--4 { grid-template-columns: repeat(4, 1fr); }
.b-gallery img { width: 100%; aspect-ratio: 1; object-fit: cover; display: block; }
.b-columns { display: grid; gap: var(--sp-12); }
@media (min-width: 860px) {
  .b-columns--2 { grid-template-columns: repeat(2, 1fr); }
  .b-columns--3 { grid-template-columns: repeat(3, 1fr); }
  .b-columns--4 { grid-template-columns: repeat(4, 1fr); }
}
.b-quote { margin: var(--sp-12) 0; max-width: 28ch; }
.b-quote blockquote { margin: 0 0 var(--sp-6); font-style: italic; }
.b-facts { display: grid; grid-template-columns: auto 1fr; gap: var(--sp-2) var(--sp-8); }
.b-facts > div { display: contents; }
.b-facts dt { color: var(--fg-muted); }
.b-facts dd { margin: 0; }
.b-a-center { text-align: center; margin-inline: auto; }
.b-a-right { text-align: right; margin-left: auto; }
.b-a-justify { text-align: justify; }
.b-w-narrow { max-width: 42ch; }
.b-w-text { max-width: 62ch; }
.b-w-wide { max-width: 90ch; }
.b-w-full { max-width: none; }
@media (max-width: 859px) { .b-only-desktop { display: none !important; } }
@media (min-width: 860px) { .b-only-mobile { display: none !important; } }
